/**
 * Centellian24 extra-credit seal, from the Figma `Badge` section `5775:81038`.
 *
 * The three variants are NOT one artwork scaled: typography, icon size and gap
 * each step differently (L→M drops the label from 15.75px to 10px but the icon
 * only from 16px to 10px, and the gap halves). They are therefore transcribed
 * one by one from their own Figma frames.
 *
 * Kept in sync with
 * `apps/mobile/src/components/ui/ProductCard/components/creditSealBadgeVariants.ts`.
 */
export type CreditSealBadgeVariant = 'l' | 'm' | 's';

export type CreditSealBadgeSpec = {
  /** Frame edge length. */
  size: number;
  /**
   * Seal artwork edge length. The seal is always concentric with the frame: it
   * overflows on L (Figma draws it at 102 offset -3, which is the same centre)
   * and is inset by 1px on M and S. 100.884 is the artwork's own artboard, so L
   * renders it 1:1 and the stroke clips at the viewBox exactly as Figma exports it.
   */
  starSize: number;
  /**
   * Rendered rim thickness in px. The smaller seals are not the large one scaled:
   * Figma's M and S artwork carries a proportionally thicker rim, so reusing one
   * path with one stroke leaves their ring ~13% thin. Values are measured from
   * the Figma PNG exports of each variant.
   */
  strokeWidthPx: number;
  iconSize: number;
  iconLabelGap: number;
  labelTopPx: number;
  labelFontSize: number;
  labelLineHeight: number;
  labelLetterSpacing: number;
  percentageTopPx: number;
  percentageFontSize: number;
  percentageLineHeight: number;
};

/**
 * `labelTopPx` / `percentageTopPx` are the Figma frame offsets. The two rows
 * overlap slightly on M and S — their line-height leading is taller than the
 * gap the design leaves — so they are positioned absolutely rather than
 * stacked, which would push the percentage 4px down.
 */
export const CREDIT_SEAL_BADGE_SPEC: Record<CreditSealBadgeVariant, CreditSealBadgeSpec> = {
  // Credit Badge/L — figma:5775-78696
  l: {
    size: 96,
    starSize: 100.884,
    strokeWidthPx: 2,
    iconSize: 16,
    iconLabelGap: 2,
    labelTopPx: 24.5,
    labelFontSize: 15.75,
    labelLineHeight: 17,
    labelLetterSpacing: 0.315,
    percentageTopPx: 41.5,
    percentageFontSize: 28.5,
    percentageLineHeight: 30,
  },
  // Credit Badge/M — figma:5775-80832
  m: {
    size: 70,
    starSize: 68,
    strokeWidthPx: 1.66,
    iconSize: 10,
    iconLabelGap: 1,
    labelTopPx: 17,
    labelFontSize: 10,
    labelLineHeight: 16,
    labelLetterSpacing: 0,
    percentageTopPx: 29,
    percentageFontSize: 16,
    percentageLineHeight: 24,
  },
  // Credit Badge/S — figma:5775-80952
  s: {
    size: 60,
    starSize: 58,
    strokeWidthPx: 1.38,
    iconSize: 10,
    iconLabelGap: 1,
    labelTopPx: 14,
    labelFontSize: 9,
    labelLineHeight: 16,
    labelLetterSpacing: 0,
    percentageTopPx: 26,
    percentageFontSize: 14,
    percentageLineHeight: 20,
  },
};

/**
 * Seal inset from the top-right corner of each surface's image box, in px,
 * exactly as measured on the Figma frames. Every inset is symmetric.
 */
export const CREDIT_SEAL_BADGE_INSET_PX = {
  /** PDP main gallery, tablet and above — figma:Version 4 696x744. */
  pdpDesktop: 36,
  /** PDP main gallery, below tablet — figma:Photo_layout 360x360. */
  pdpMobile: 20,
  /** Vertical card image box, tablet and above — figma:Card_picture 230x300. */
  cardDesktop: 6,
  /** Vertical card image box below tablet — figma:Card_Picture 158x200. */
  cardCompact: 8,
  /**
   * Horizontal card image box. Figma insets this by 8 too, but its card art is
   * 164px wide where ours is 132px: at 8 the seal's box overlaps the top-left
   * discount badge by ~1px. 4 restores a positive gap without shrinking the seal.
   */
  cardHorizontal: 4,
} as const;

/**
 * Stacking order for the seal over a product image. Matches the discount badge's
 * layer so neither can slip under the image or the wishlist/add-to-cart slots.
 */
export const CREDIT_SEAL_BADGE_Z_INDEX = 3;

/** Seal stroke expressed in the artwork's own viewBox units. */
export const creditSealStrokeInViewBox = (spec: CreditSealBadgeSpec, viewBoxSize: number): number =>
  (spec.strokeWidthPx * viewBoxSize) / spec.starSize;

/** Top-left offset that centres the seal artwork inside its frame. */
export const creditSealStarOffset = (spec: CreditSealBadgeSpec): number => (spec.size - spec.starSize) / 2;

/**
 * Edge length of the region a parity screenshot must cover: the frame, or the
 * seal artwork when it overflows. Matches the Figma PNG export bounds exactly
 * (L exports at 101, M at 70, S at 60).
 */
export const creditSealCaptureSize = (spec: CreditSealBadgeSpec): number => Math.max(spec.size, spec.starSize);
